import {
  Box,
  IconButton,
  InputAdornment,
  OutlinedInput as MuiOutlinedInput,
  OutlinedInputProps,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import styled from "styled-components";
import UpdatedBadge from "./UpdatedBadge";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { inToFtAndIn } from "../../../questionnaire/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";
import { MAX_HEIGHT_FT, MAX_HEIGHT_IN } from "../../../questionnaire/constant";
import { Theme } from "../../../../providers/ThemeProvider";

// Styled OutlinedInput
const StyledOutlinedInput = styled(MuiOutlinedInput)<OutlinedInputProps>(
  () => ({
    height: "56px",
    width: "100%",
    borderRadius: "8px",
    "& .MuiOutlinedInput-input": {
      padding: "24px 0 8px 12px",
      display: "flex",
      alignItems: "center",
      fontFamily: "var(--font-Medium)",
      fontSize: "12px",
      width: "10%",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    legend: {
      display: "none",
    },
    fieldset: {
      top: 0,
    },
  })
);

const StyledInputLabel = styled(Typography)(({ color }) => ({
  fontFamily: "var(--font-Medium)",
  fontSize: "10px",
  backgroundColor: "transparent",
  transition: "transform 0.2s ease-in-out",
  color: color,

  position: "absolute",
  top: "10px",
  left: "12px",
}));

const DoubleArrowElement = ({
  inputRef,
  category,
  focused,
  max,
  min,
  changeValue,
  value,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  category: string;
  focused: boolean;
  max: number;
  min: number;
  changeValue: number;
  value: number;
}) => {
  const theme: Theme = useTheme();
  const answers = useSelector((state: RootStateAnswers) => state.answers);
  const dispatch = useDispatch();
  const handleChangeNumeric = (changeValue: number) => {
    inputRef.current?.focus();
    let newValue = answers.biometricQuest.myAnswers[category] + changeValue;

    if (newValue === answers.biometricOldAnswers[category]) {
      if (answers.biometric_modified_fields.indexOf(category) !== -1) {
        dispatch(answersActions.removeBiometricModifiedFields(category));
      }
    } else {
      if (answers.biometric_modified_fields.indexOf(category) === -1) {
        dispatch(answersActions.addBiometricModifiedFields(category));
      }
    }

    dispatch(
      answersActions.updateBiometricAnswers({
        [category]: newValue,
      })
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // marginLeft: "-28px",
        position: "absolute",
        top: "50%",
        right: "4px",
        transform: "translateY(-50%)",
        zIndex: 50,
      }}
    >
      <IconButton
        aria-label="increase"
        onClick={() => handleChangeNumeric(changeValue)}
        disabled={value >= max}
        sx={{
          padding: "0",
          width: "21px",
          height: "21px",
          color: focused
            ? theme.colors_ds.PRIMARY_ICON_MAIN
            : theme.colors_ds.NEUTRAL_TEXT_WEAK,
        }}
      >
        <KeyboardArrowUpIcon sx={{ fontSize: "21px", color: "inherit" }} />
      </IconButton>
      <IconButton
        aria-label="decrease"
        onClick={() => handleChangeNumeric(-changeValue)}
        disabled={value <= min}
        sx={{
          padding: "0",
          width: "21px",
          height: "21px",
          color: focused
            ? theme.colors_ds.PRIMARY_ICON_MAIN
            : theme.colors_ds.NEUTRAL_TEXT_WEAK,
        }}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: "21px", color: "inherit" }} />
      </IconButton>
    </Box>
  );
};

const CustomInputFieldDoubleNumeric = ({
  category,
  setOpenPopperId,
}: {
  category: string;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const answers = useSelector((state: RootStateAnswers) => state.answers);

  const handleValue = (cat: string) => {
    const imperialValues = inToFtAndIn(
      Math.round(answers.biometricQuest.myAnswers["height"])
    );
    if (cat === "ft") {
      return imperialValues.value1;
    } else {
      return imperialValues.value2;
    }
  };

  const handleChange = async (event: any, cat: string) => {
    let newValue = +event.target.value;

    if (cat === "ft") {
      if (newValue > MAX_HEIGHT_FT || isNaN(newValue)) {
        return;
      }
      newValue = newValue * 12 + handleValue("in");
    } else {
      if (newValue > MAX_HEIGHT_IN || isNaN(newValue)) {
        return;
      }
      newValue = handleValue("ft") * 12 + newValue;
    }

    dispatch(
      answersActions.updateBiometricAnswers({
        [category]: newValue,
      })
    );
    if (newValue === answers.biometricOldAnswers[category]) {
      if (answers.biometric_modified_fields.indexOf(category) !== -1) {
        dispatch(answersActions.removeBiometricModifiedFields(category));
      }
    } else {
      if (answers.biometric_modified_fields.indexOf(category) === -1) {
        dispatch(answersActions.addBiometricModifiedFields(category));
      }
    }
  };

  const isUpdated =
    [
      ...answers.biometric_modified_fields,
      ...answers.personalInfoModifiedFields,
    ].indexOf(category) !== -1;

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        border: `1px solid ${
          focused
            ? theme.colors_ds.PRIMARY_ICON_MAIN
            : theme.colors_ds.NEUTRAL_BORDER_WEAK
        }`,
        borderRadius: "8px",
        position: "relative",
        display: "flex",
      }}
    >
      <StyledInputLabel
        color={
          focused
            ? theme.colors_ds.PRIMARY_ICON_MAIN
            : theme.colors_ds.NEUTRAL_TEXT_WEAK
        }
      >
        Height, ft
        {isUpdated && <UpdatedBadge />}
      </StyledInputLabel>

      <Box
        sx={{
          position: "relative",
          flex: 1,
        }}
      >
        <StyledOutlinedInput
          type={"numeric"}
          value={handleValue("ft")}
          onFocus={() => {
            setFocused(true);
            setOpenPopperId(null);
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => handleChange(e, "ft")}
          inputRef={inputRef}
          theme={theme}
          endAdornment={
            <InputAdornment position="end" sx={{ margin: "0" }}>
              <span
                style={{
                  fontFamily: "var(--font-Medium)",
                  fontSize: "12px",
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                  paddingTop: "16px",
                }}
              >
                ft
              </span>
            </InputAdornment>
          }
        />
        <DoubleArrowElement
          inputRef={inputRef}
          category={category}
          focused={focused}
          max={MAX_HEIGHT_FT}
          min={0}
          changeValue={12}
          value={handleValue("ft")}
        />
      </Box>
      <Box
        sx={{
          position: "relative",
          flex: 1,
        }}
      >
        <StyledOutlinedInput
          type={"numeric"}
          value={handleValue("in")}
          onFocus={() => {
            setFocused(true);
            setOpenPopperId(null);
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => handleChange(e, "in")}
          inputRef={inputRef}
          theme={theme}
          endAdornment={
            <InputAdornment position="end" sx={{ margin: "0" }}>
              <span
                style={{
                  fontFamily: "var(--font-Medium)",
                  fontSize: "12px",
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                  paddingTop: "16px",
                }}
              >
                in
              </span>
            </InputAdornment>
          }
        />
        <DoubleArrowElement
          inputRef={inputRef}
          category={category}
          focused={focused}
          max={MAX_HEIGHT_IN}
          min={0}
          changeValue={1}
          value={handleValue("in")}
        />
      </Box>

    </Box>
  );
};

export default CustomInputFieldDoubleNumeric;
